import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import systemStatusService from "../../services/systemStatus.service";

const SERVICES = [
    { key: "API", label: "EyeconX-API", icon: "bi-hdd-network", blurb: "HTTP API" },
    { key: "GATEWAY", label: "EyeconX-Gateway-API", icon: "bi-broadcast", blurb: "SMS gateway API" },
    { key: "ICONX_LINK", label: "IconXLinks", icon: "bi-link-45deg", blurb: "URL shortener service" },
    { key: "REMINDER", label: "EyeconX-Reminder", icon: "bi-alarm", blurb: "Scheduler workers" },
    { key: "PROCESSOR", label: "EyeconX-Processor", icon: "bi-gear-wide-connected", blurb: "Queue workers" },
];

const SERVICE_STATUS_TEXT = {
    UP: "Operational",
    DEGRADED: "Degraded",
    DOWN: "Down",
    UNKNOWN: "No data",
};

const INSTANCE_STATUS_TEXT = {
    UP: "Up",
    DEGRADED: "Degraded",
    DOWN: "Down",
    UNKNOWN: "No data",
};

function formatCheckedAt(value) {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString();
}

function overallOf(service) {
    if (service?.overallStatus) {
        return service.overallStatus;
    }
    if (!service) {
        return "UNKNOWN";
    }
    const up = service.upCount || 0;
    const degraded = service.degradedCount || 0;
    const down = service.downCount || 0;
    if (down === 0 && degraded === 0 && up > 0) {
        return "UP";
    }
    if (up === 0 && degraded === 0 && down > 0) {
        return "DOWN";
    }
    if (up + degraded + down === 0) {
        return "UNKNOWN";
    }
    return "DEGRADED";
}

function aggregate(services) {
    const statuses = services.map(overallOf);
    if (statuses.length === 0) {
        return { key: "unknown", title: "Awaiting status", subtitle: "No services have reported yet." };
    }
    const anyDown = statuses.includes("DOWN");
    const anyDegraded = statuses.includes("DEGRADED");
    const allUp = statuses.every((s) => s === "UP");
    if (allUp) {
        return { key: "operational", title: "All systems operational", subtitle: "Every service instance is up." };
    }
    if (anyDown) {
        const allDown = statuses.every((s) => s === "DOWN" || s === "UNKNOWN");
        return allDown
            ? { key: "outage", title: "Major outage", subtitle: "One or more services are fully down." }
            : { key: "outage", title: "Partial outage", subtitle: "Some instances are down." };
    }
    if (anyDegraded) {
        return { key: "degraded", title: "Degraded performance", subtitle: "Some instances report failing checks." };
    }
    return { key: "unknown", title: "Status unknown", subtitle: "No instances have reported." };
}

const AGG_ICON = {
    operational: "bi-check-circle-fill",
    degraded: "bi-exclamation-triangle-fill",
    outage: "bi-x-octagon-fill",
    unknown: "bi-question-circle-fill",
};

function checkChip(label, value) {
    let tone = "muted";
    let text = "n/a";
    if (value === true) {
        tone = "ok";
        text = "OK";
    } else if (value === false) {
        tone = "fail";
        text = "Fail";
    }
    return (
        <span className={`ss-check ss-check--${tone}`}>
            <span className="ss-check__label">{label}</span>
            <span className="ss-check__value">{text}</span>
        </span>
    );
}

function SystemStatus() {
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStatus = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const res = await systemStatusService.getSystemStatus();
            setStatus(res.data);
        } catch (err) {
            setError("Unable to load system status.");
            setStatus(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const byKey = useMemo(() => {
        const map = {};
        (status?.services || []).forEach((service) => {
            map[service.service] = service;
        });
        return map;
    }, [status]);

    const agg = useMemo(() => aggregate(status?.services || []), [status]);

    return (
        <div className="container-fluid ss-page">
            <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h1>
                    <i className="bi bi-activity"></i> System Status
                </h1>
                <Button variant="outline-primary" size="sm" onClick={fetchStatus} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Checking…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Refresh
                        </>
                    )}
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
            {status?.pingError && (
                <Alert variant="warning" className="mb-3">
                    RabbitMQ ping error: {status.pingError}
                </Alert>
            )}

            <div className={`ss-hero ss-hero--${agg.key}`}>
                <div className="ss-hero__icon">
                    <i className={`bi ${AGG_ICON[agg.key]}`}></i>
                </div>
                <div className="ss-hero__text">
                    <div className="ss-hero__title">{agg.title}</div>
                    <div className="ss-hero__subtitle">{agg.subtitle}</div>
                </div>
                {status && (
                    <div className="ss-hero__meta">
                        <div>
                            <i className="bi bi-clock-history me-1"></i>
                            {formatCheckedAt(status.checkedAt)}
                        </div>
                        {status.checkedByWorkerId && (
                            <div className="ss-hero__meta-sub">via {status.checkedByWorkerId}</div>
                        )}
                        {!status.pingPublished && (
                            <div className="ss-hero__meta-warn">Ping not published — only this API checked</div>
                        )}
                    </div>
                )}
            </div>

            <div className="ss-grid">
                {SERVICES.map((meta) => {
                    const service = byKey[meta.key];
                    const overall = overallOf(service);
                    const tone = overall.toLowerCase();
                    const instances = service?.instances || [];
                    return (
                        <section key={meta.key} className={`ss-card ss-card--${tone}`}>
                            <header className="ss-card__head">
                                <div className="ss-card__icon">
                                    <i className={`bi ${meta.icon}`}></i>
                                </div>
                                <div className="ss-card__heading">
                                    <div className="ss-card__name">{meta.label}</div>
                                    <div className="ss-card__blurb">{meta.blurb}</div>
                                </div>
                                <span className={`ss-badge ss-badge--${tone}`}>
                                    <span className="ss-dot"></span>
                                    {SERVICE_STATUS_TEXT[overall]}
                                </span>
                            </header>

                            <div className="ss-card__stats">
                                <span className="ss-stat ss-stat--up">
                                    <strong>{service?.upCount ?? 0}</strong> up
                                </span>
                                <span className="ss-stat ss-stat--degraded">
                                    <strong>{service?.degradedCount ?? 0}</strong> degraded
                                </span>
                                <span className="ss-stat ss-stat--down">
                                    <strong>{service?.downCount ?? 0}</strong> down
                                </span>
                            </div>

                            <ul className="ss-instances">
                                {instances.length === 0 && (
                                    <li className="ss-instances__empty">
                                        {isLoading
                                            ? "Checking…"
                                            : "No instances replied to the health ping."}
                                    </li>
                                )}
                                {instances.map((instance) => {
                                    const itone = (instance.status || "DOWN").toLowerCase();
                                    return (
                                        <li key={`${meta.key}-${instance.workerId}`} className="ss-instance">
                                            <span className={`ss-dot ss-dot--${itone}`}></span>
                                            <div className="ss-instance__info">
                                                <div className="ss-instance__id">{instance.workerId}</div>
                                                <div className="ss-instance__host">
                                                    {instance.hostname || (instance.error ? instance.error : "no reply")}
                                                </div>
                                            </div>
                                            <div className="ss-instance__right">
                                                <span className={`ss-instance__status ss-instance__status--${itone}`}>
                                                    {INSTANCE_STATUS_TEXT[instance.status] || instance.status}
                                                </span>
                                                <div className="ss-instance__checks">
                                                    {checkChip("DB", instance.databaseOk)}
                                                    {checkChip("MQ", instance.rabbitMqOk)}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    );
                })}
            </div>
        </div>
    );
}

export default SystemStatus;
